import type {ListItemViewModel} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import {
  EventTriggerConfig,
  Routine,
  RoutineStatus,
  TimeTriggerConfig,
  TriggerType,
} from 'llm/routines/RoutineTypes';
import {formatEventTrigger, formatTimeTrigger} from 'llm/routines/RoutineUtils';
import type {MantineOverflowMenuOption} from 'ui-library/menus/MantineOverflowMenu';
import * as Logging from 'util/Logging';

const UNTITLED_AGENT_NAME = 'Untitled Agent';

export default function buildListItemViewModelFromAgent(
  routine: Routine,
  handlers: {
    onToggleActive: (routine: Routine, active: boolean) => Promise<void>;
    onRun: (routine: Routine) => Promise<void>;
    onArchive: (routine: Routine) => void;
  }
): ListItemViewModel {
  const isTimeBased = routine.triggerType === TriggerType.TIME_BASED;
  const displayName = routine.name ?? UNTITLED_AGENT_NAME;
  // PENDING_REGISTRATION is treated as active (matches the legacy listing), so the toggle is only
  // off when the agent is explicitly paused.
  const isActive = routine.status !== RoutineStatus.PAUSED;
  const triggerText = isTimeBased
    ? formatTimeTrigger(routine.triggerConfig as TimeTriggerConfig)
    : formatEventTrigger(routine.triggerConfig as EventTriggerConfig);

  const items: Array<MantineOverflowMenuOption> = [];
  if (isTimeBased) {
    items.push({
      name: 'Run now',
      onSelect: () => {
        // The overflow menu's onSelect is synchronous and can't propagate a rejection to a global
        // handler. `onRun` surfaces known server errors (e.g. the execution rate limit) as a toast
        // and re-throws only unexpected errors, so report those here instead of silently
        // discarding them.
        handlers.onRun(routine).catch((error: unknown) => {
          Logging.error(error instanceof Error ? error : new Error(String(error)), {
            extra: {routineId: routine.id},
          });
        });
      },
    });
  }
  items.push({
    name: 'Archive',
    variant: 'destructive',
    onSelect: () => {
      handlers.onArchive(routine);
    },
  });

  return {
    id: routine.id,
    title: displayName,
    subtitle: triggerText,
    description: routine.prompt,
    toggle: {
      checked: isActive,
      ariaLabel: isActive ? `Pause ${displayName}` : `Resume ${displayName}`,
      tooltip: isActive ? 'Pause agent' : 'Resume agent',
      onChange: (active) => handlers.onToggleActive(routine, active),
    },
    menu: [{items}],
  };
}
